<script lang="ts" setup>
interface Props {
  slides?: { image?: string; alt?: string }[]
  naturist?: boolean
  showDisclaimer?: boolean
  eagerLoadFirstImage?: boolean
  sizes?: string
  rounded?: boolean
  height?: number | string
  gallery?: boolean
}

const props = defineProps<Props>()
const currentIndex = ref(0)
const contentRef = ref()
const contentRefWidth = computed(() => contentRef.value?.$el.offsetWidth)
const lastAction = ref<'next' | 'previous' | null>(null)
const isFullscreen = ref(false)

const { lengthX } = useSwipe(contentRef, {
  onSwipeEnd() {
    if (
      lengthX.value < 0 &&
      contentRefWidth.value &&
      Math.abs(lengthX.value) / contentRefWidth.value >= 0.15
    ) {
      previous()
    }

    if (
      lengthX.value > 0 &&
      contentRefWidth.value &&
      Math.abs(lengthX.value) / contentRefWidth.value >= 0.15
    ) {
      next()
    }
  },
})

const next = () => {
  if (!props.slides) {
    return
  }

  lastAction.value = 'next'
  currentIndex.value = (currentIndex.value + 1) % props.slides.length
  useGaSharedFunction.gtagEvent('browse_photos')
}

const previous = () => {
  if (!props.slides) {
    return
  }

  lastAction.value = 'previous'
  currentIndex.value =
    (currentIndex.value - 1 + props.slides.length) % props.slides.length
  useGaSharedFunction.gtagEvent('browse_photos')
}

const handleKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'ArrowLeft':
      previous()
      break
    case 'ArrowRight':
      next()
      break
    case 'Escape':
      isFullscreen.value = false
      break
  }
}

const isNextItem = (index: number) => {
  return (
    props.slides && index === (currentIndex.value + 1) % props.slides.length
  )
}

const isPreviousItem = (index: number) => {
  return (
    props.slides &&
    index ===
      (currentIndex.value - 1 + props.slides.length) % props.slides.length
  )
}

watch(isFullscreen, () => {
  if (isFullscreen.value) {
    useGaSharedFunction.gtagEvent('open_photos')
    addEventListener('keydown', handleKeydown)
  } else {
    removeEventListener('keydown', handleKeydown)
  }
})

onUnmounted(() => {
  removeEventListener('keydown', handleKeydown)
})

const toggle = () => {
  isFullscreen.value = !isFullscreen.value
}

const showGallery = computed(
  () => props.gallery && props.slides && props.slides?.length > 8
)
</script>

<template>
  <ImageCarouselContent
    ref="contentRef"
    :naturist="naturist"
    :show-disclaimer="showDisclaimer"
    :is-fullscreen="isFullscreen"
    :count="slides?.length"
    :current-index="currentIndex"
    :rounded="rounded"
    :gallery="showGallery"
    @next="next"
    @previous="previous"
    @toggle-fullscreen="toggle"
  >
    <template #gallery>
      <ImageCarouselGallery
        v-if="showGallery"
        :slides="slides?.slice(0, 9)"
        :sizes="sizes"
        :rounded="rounded"
        @toggle="
          (index) => {
            currentIndex = index
            toggle()
          }
        "
      />
    </template>

    <slot :current-index="currentIndex">
      <ImageCarouselItem
        v-for="(image, index) in slides"
        v-show="currentIndex === index"
        :key="index"
        :last-action="lastAction"
        :is-next-item="isNextItem(index)"
        :is-previous-item="isPreviousItem(index)"
        :is-fullscreen="isFullscreen"
        :item="image"
        :index="index"
        :sizes="sizes"
        :current-index="currentIndex"
        :height="height"
        :eager-load-first-image="eagerLoadFirstImage"
      />
    </slot>
  </ImageCarouselContent>
</template>
